import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { History, HISTORY_TABS, type HistoryTab } from "@/features/logs";

/**
 * HISTORY — the third tab.
 *
 * ⚠️ WHY `.index` AND NOT `_app.history.tsx`, WHICH IS WHAT THIS FILE USED TO BE.
 *
 * 🚨 THAT FILENAME MADE THIS SCREEN THE LAYOUT OF `/history/$id`, and the symptom was exactly
 * what you would expect and nothing like a crash: tapping a session changed the URL, the page
 * scrolled to the top, and the history list stayed on screen. TanStack Router had matched the
 * child route and rendered its parent — this file — which draws no `<Outlet />`, so the
 * session it matched had nowhere to appear.
 *
 * Taking the index position leaves `/history` addressing exactly this screen and `/history/$id`
 * addressing itself. It is the same correction `_app.session.$id.index.tsx` records, for the
 * same reason, and it is the second time this shape has caught somebody out.
 */
export const Route = createFileRoute("/_app/history/")({
  /**
   * ═══ 🚨 WHICH TAB IS SHOWING IS A PLACE, SO IT LIVES IN THE URL ═══
   *
   * It was `defaultValue="sessions"` inside the component, which meant it lasted exactly as
   * long as the component did. Opening a benchmark or a movement PUSHES a screen and unmounts
   * this one, so coming back mounted it fresh on the first tab — an athlete browsing their
   * movements tapped one, closed it, and was returned to Sessions.
   *
   * In the search params, going back restores it with no code at all: the previous history
   * entry already carries `?tab=movements`, and the router hands it straight back.
   *
   * ⚠️ THE DEFAULT IS OMITTED RATHER THAN WRITTEN OUT. Returning `{}` for the first tab keeps
   * `/history` as the canonical address of this screen — the bottom bar links to it, deep links
   * arrive at it, and `?tab=sessions` on all of them would be noise that says nothing.
   *
   * ⚠️ AND `tab` IS OPTIONAL IN THE OUTPUT TYPE, which is what keeps `navigate({ to: "/history" })`
   * legal everywhere it already appears — the tab bar's link included. Required here means every
   * one of those call sites has to name a tab it does not care about.
   */
  validateSearch: (search: Record<string, unknown>): { tab?: HistoryTab } => {
    const tab = HISTORY_TABS.find((one) => one === search.tab);
    return tab === undefined || tab === "sessions" ? {} : { tab };
  },
  component: HistoryScreen,
});

function HistoryScreen() {
  const { t } = useTranslation("logs");
  const { tab = "sessions" } = Route.useSearch();
  const navigate = Route.useNavigate();

  return (
    <div className="flex flex-col gap-7">
      <h1 className="text-title">{t("history.title")}</h1>
      <History
        tab={tab}
        /**
         * 🔑 `replace`, BECAUSE CHANGING TAB IS NOT GOING SOMEWHERE. Pushing an entry per tap
         * would make the back gesture walk Movements → Benchmarks → Sessions before it left
         * History at all, which on a phone reads as a screen that will not let you out.
         */
        onTabChange={(next) => void navigate({ search: { tab: next }, replace: true })}
      />
    </div>
  );
}
